"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ApiWaveCurationGroup } from "@/generated/models/ApiWaveCurationGroup";
import { useWave } from "@/hooks/useWave";
import type { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { PlusIcon } from "@heroicons/react/24/solid";
import React, { useContext, useMemo } from "react";
import { Tooltip } from "react-tooltip";
import { getWaveDropEligibility } from "../dropEligibility";
import type { LeaderboardViewMode } from "../types";
import { WaveLeaderboardCurationGroupSelect } from "./WaveLeaderboardCurationGroupSelect";
import { useLeaderboardHeaderControlMeasurements } from "./useLeaderboardHeaderControlMeasurements";
import { WAVE_LEADERBOARD_SORT_ITEMS, WaveleaderboardSort } from "./WaveleaderboardSort";
import { resolveWaveLeaderboardHeaderControlModes } from "./waveLeaderboardHeaderControls";
interface WaveLeaderboardHeaderProps {
  readonly wave: ApiWave;
  readonly onCreateDrop?: (() => void) | undefined;
  readonly viewMode: LeaderboardViewMode;
  readonly onViewModeChange: (mode: LeaderboardViewMode) => void;
  readonly sort: WaveDropsLeaderboardSort;
  readonly onSortChange: (sort: WaveDropsLeaderboardSort) => void;
  readonly curationGroups?: readonly ApiWaveCurationGroup[] | undefined;
  readonly curatedByGroupId?: string | null | undefined;
  readonly onCurationGroupChange?: ((groupId: string | null) => void) | undefined;
}
export const WaveLeaderboardHeader: React.FC<WaveLeaderboardHeaderProps> = t0 => {
  const $ = _c(73);
  const {
    wave,
    onCreateDrop,
    viewMode: t1,
    onViewModeChange,
    sort,
    onSortChange,
    curationGroups: t2,
    curatedByGroupId: t3,
    onCurationGroupChange
  } = t0;
  const viewMode = t1 === undefined ? "list" : t1;
  let t4;
  if ($[0] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const curationGroups = t4;
  const curatedByGroupId = t3 === undefined ? null : t3;
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    isMemesWave,
    isCurationWave,
    participation
  } = useWave(wave);
  const isLoggedIn = Boolean(connectedProfile?.handle);
  const t5 = Boolean(activeProfileProxy);
  let t6;
  if ($[2] !== isCurationWave || $[3] !== isLoggedIn || $[4] !== participation || $[5] !== t5) {
    t6 = getWaveDropEligibility({
      isLoggedIn,
      isProxy: t5,
      isCurationWave,
      participation
    });
    $[2] = isCurationWave;
    $[3] = isLoggedIn;
    $[4] = participation;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const {
    canCreateDrop
  } = t6;
  const showCurationGroupSelect = Boolean(onCurationGroupChange && curationGroups.length > 0);
  const viewModes = isMemesWave ? ["list", "grid"] : ["list", "grid", "grid_content_only"];
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = new Map(WAVE_LEADERBOARD_SORT_ITEMS.map(_temp));
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const sortLabelByValue = t7;
  let t8;
  if ($[8] !== sort) {
    t8 = sortLabelByValue.get(sort) ?? "Current Vote";
    $[8] = sort;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const activeSortLabel = t8;
  let t9;
  bb0: {
    if (!showCurationGroupSelect || !curatedByGroupId) {
      t9 = "All submissions";
      break bb0;
    }
    let t10;
    if ($[10] !== curatedByGroupId || $[11] !== curationGroups) {
      t10 = curationGroups.find(group => group.id === curatedByGroupId)?.name ?? "All submissions";
      $[10] = curatedByGroupId;
      $[11] = curationGroups;
      $[12] = t10;
    } else {
      t10 = $[12];
    }
    t9 = t10;
  }
  const activeCurationLabel = t9;
  let t10;
  if ($[13] !== curationGroups) {
    t10 = curationGroups.map(_temp2).join("|");
    $[13] = curationGroups;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const curationProbeKey = t10;
  const t11 = `${activeSortLabel}|${activeCurationLabel}|${curationProbeKey}`;
  let t12;
  if ($[15] !== showCurationGroupSelect || $[16] !== t11) {
    t12 = {
      showCurationGroupSelect,
      remeasureKey: t11
    };
    $[15] = showCurationGroupSelect;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const {
    controlsRowRef,
    viewModeTabsRef,
    sortTabsProbeRef,
    sortDropdownProbeRef,
    curationTabsProbeRef,
    curationDropdownProbeRef,
    measurements
  } = useLeaderboardHeaderControlMeasurements(t12);
  let t13;
  if ($[18] !== measurements.availableWidth || $[19] !== measurements.curationDropdownWidth || $[20] !== measurements.curationTabsWidth || $[21] !== measurements.sortDropdownWidth || $[22] !== measurements.sortTabsWidth || $[23] !== measurements.viewModesWidth || $[24] !== showCurationGroupSelect) {
    t13 = resolveWaveLeaderboardHeaderControlModes({
      availableWidth: measurements.availableWidth,
      viewModesWidth: measurements.viewModesWidth,
      sortTabsWidth: measurements.sortTabsWidth,
      sortDropdownWidth: measurements.sortDropdownWidth,
      hasCurationControl: showCurationGroupSelect,
      curationTabsWidth: measurements.curationTabsWidth,
      curationDropdownWidth: measurements.curationDropdownWidth
    });
    $[18] = measurements.availableWidth;
    $[19] = measurements.curationDropdownWidth;
    $[20] = measurements.curationTabsWidth;
    $[21] = measurements.sortDropdownWidth;
    $[22] = measurements.sortTabsWidth;
    $[23] = measurements.viewModesWidth;
    $[24] = showCurationGroupSelect;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const controlModes = t13;
  const getViewModeLabel = _temp3;
  const getViewModeTabClass = _temp4;
  const getViewModeIcon = _temp5;
  const t14 = "tw-relative tw-flex tw-flex-col tw-gap-y-4 tw-bg-black tw-@container";
  const t15 = "tw-flex tw-items-start tw-gap-2";
  const t16 = "leaderboard-header-controls-row";
  const t17 = `tw-flex tw-min-w-0 tw-flex-1 tw-flex-nowrap tw-items-center tw-gap-2 ${controlModes.enableControlsScroll ? "horizontal-menu-hide-scrollbar tw-overflow-x-auto tw-scrollbar-thin tw-scrollbar-track-transparent tw-scrollbar-thumb-iron-700/60" : "tw-overflow-x-hidden"}`;
  const t18 = "tablist";
  const t19 = "Leaderboard view modes";
  const t20 = "tw-flex tw-flex-shrink-0 tw-gap-0.5 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-p-1";
  const t21 = viewModes.map(mode_2 => <React.Fragment key={mode_2}><button type="button" role="tab" aria-label={getViewModeLabel(mode_2)} aria-selected={viewMode === mode_2} tabIndex={viewMode === mode_2 ? 0 : -1} className={getViewModeTabClass(mode_2, viewMode)} onClick={() => onViewModeChange(mode_2)} data-tooltip-id={`leaderboard-view-mode-${mode_2}-${wave.id}`}>{getViewModeIcon(mode_2)}<span className="tw-sr-only">{getViewModeLabel(mode_2)}</span></button><Tooltip id={`leaderboard-view-mode-${mode_2}-${wave.id}`} place="top" offset={8} opacity={1} style={{
      padding: "4px 8px",
      background: "#37373E",
      color: "white",
      fontSize: "13px",
      fontWeight: 500,
      borderRadius: "6px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      zIndex: 99999,
      pointerEvents: "none"
    }}>{getViewModeLabel(mode_2)}</Tooltip></React.Fragment>);
  let t22;
  if ($[26] !== t21 || $[27] !== viewModeTabsRef) {
    t22 = <div ref={viewModeTabsRef} role={t18} aria-label={t19} className={t20}>{t21}</div>;
    $[26] = t21;
    $[27] = viewModeTabsRef;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] !== controlModes.sortMode || $[30] !== onSortChange || $[31] !== sort) {
    t23 = <div className="tw-flex-shrink-0"><WaveleaderboardSort sort={sort} onSortChange={onSortChange} mode={controlModes.sortMode} /></div>;
    $[29] = controlModes.sortMode;
    $[30] = onSortChange;
    $[31] = sort;
    $[32] = t23;
  } else {
    t23 = $[32];
  }
  let t24;
  if ($[33] !== controlModes.curationMode || $[34] !== curatedByGroupId || $[35] !== curationGroups || $[36] !== onCurationGroupChange || $[37] !== showCurationGroupSelect) {
    t24 = showCurationGroupSelect && onCurationGroupChange && <div className="tw-flex-shrink-0"><WaveLeaderboardCurationGroupSelect groups={curationGroups} selectedGroupId={curatedByGroupId} onChange={onCurationGroupChange} mode={controlModes.curationMode} /></div>;
    $[33] = controlModes.curationMode;
    $[34] = curatedByGroupId;
    $[35] = curationGroups;
    $[36] = onCurationGroupChange;
    $[37] = showCurationGroupSelect;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== controlsRowRef || $[40] !== t17 || $[41] !== t22 || $[42] !== t23 || $[43] !== t24) {
    t25 = <div ref={controlsRowRef} data-testid={t16} className={t17}>{t22}{t23}{t24}</div>;
    $[39] = controlsRowRef;
    $[40] = t17;
    $[41] = t22;
    $[42] = t23;
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== canCreateDrop || $[46] !== isLoggedIn || $[47] !== isMemesWave || $[48] !== onCreateDrop) {
    t26 = isLoggedIn && <div className={`tw-flex tw-flex-col tw-items-end ${isMemesWave ? "lg:tw-hidden" : ""}`}>{canCreateDrop && onCreateDrop && <PrimaryButton loading={false} disabled={false} onClicked={onCreateDrop} padding="tw-px-3 tw-py-2"><PlusIcon className="-tw-ml-1 tw-h-4 tw-w-4 tw-flex-shrink-0" /><span>Drop</span></PrimaryButton>}</div>;
    $[45] = canCreateDrop;
    $[46] = isLoggedIn;
    $[47] = isMemesWave;
    $[48] = onCreateDrop;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t25 || $[51] !== t26) {
    t27 = <div className={t15}>{t25}{t26}</div>;
    $[50] = t25;
    $[51] = t26;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = WAVE_LEADERBOARD_SORT_ITEMS.map(_temp6);
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  let t29;
  if ($[54] !== sortTabsProbeRef) {
    t29 = <div ref={sortTabsProbeRef} className="tw-inline-flex tw-items-center tw-gap-x-1 tw-rounded-lg tw-bg-iron-950 tw-p-1 tw-ring-1 tw-ring-inset tw-ring-iron-700">{t28}</div>;
    $[54] = sortTabsProbeRef;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <span className="tw-font-semibold tw-text-iron-500">Sort: </span>;
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  let t31;
  if ($[57] !== activeSortLabel || $[58] !== sortDropdownProbeRef) {
    t31 = <div ref={sortDropdownProbeRef} className="tailwind-scope tw-inline-flex tw-whitespace-nowrap tw-rounded-lg tw-py-2.5 tw-pl-3.5 tw-pr-8 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset tw-ring-iron-700">{t30}{activeSortLabel}</div>;
    $[57] = activeSortLabel;
    $[58] = sortDropdownProbeRef;
    $[59] = t31;
  } else {
    t31 = $[59];
  }
  let t32;
  if ($[60] !== activeCurationLabel || $[61] !== curationDropdownProbeRef || $[62] !== curationGroups || $[63] !== curationTabsProbeRef || $[64] !== showCurationGroupSelect) {
    t32 = showCurationGroupSelect && <><div ref={curationTabsProbeRef} className="tw-inline-flex tw-items-center tw-gap-x-1"><div className="tw-flex tw-items-center tw-gap-x-1.5 tw-whitespace-nowrap tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset tw-ring-iron-600">All submissions</div>{curationGroups.map(_temp7)}</div><div ref={curationDropdownProbeRef} className="tailwind-scope tw-inline-flex tw-whitespace-nowrap tw-rounded-lg tw-py-2.5 tw-pl-3.5 tw-pr-8 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset tw-ring-iron-700"><span className="tw-font-semibold tw-text-iron-500">Group: </span>{activeCurationLabel}</div></>;
    $[60] = activeCurationLabel;
    $[61] = curationDropdownProbeRef;
    $[62] = curationGroups;
    $[63] = curationTabsProbeRef;
    $[64] = showCurationGroupSelect;
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  let t33;
  if ($[66] !== t29 || $[67] !== t31 || $[68] !== t32) {
    t33 = <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-0 tw-top-0 tw-h-0 tw-overflow-hidden tw-whitespace-nowrap tw-opacity-0">{t29}{t31}{t32}</div>;
    $[66] = t29;
    $[67] = t31;
    $[68] = t32;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== t27 || $[71] !== t33) {
    t34 = <div className={t14}>{t27}{t33}</div>;
    $[70] = t27;
    $[71] = t33;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  return t34;
};
function _temp(item) {
  return [item.value, item.label];
}
function _temp2(group_0) {
  return `${group_0.id}:${group_0.name}`;
}
function _temp3(mode) {
  if (mode === "list") {
    return "List view";
  }
  if (mode === "grid") {
    return "Grid view";
  }
  return "Content only";
}
function _temp4(mode_0, activeMode) {
  if (activeMode === mode_0) {
    return "tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-transparent tw-transition-colors tw-border-primary-500/50 tw-bg-primary-600/20 tw-text-primary-400";
  }
  return "tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-transparent tw-transition-colors tw-bg-transparent tw-text-iron-500 desktop-hover:hover:tw-bg-white/5 desktop-hover:hover:tw-text-iron-300";
}
function _temp5(mode_1) {
  if (mode_1 === "list") {
    return <svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="tw-size-5 tw-flex-shrink-0"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" /></svg>;
  }
  if (mode_1 === "grid") {
    return <svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="tw-size-5 tw-flex-shrink-0"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" /></svg>;
  }
  return <svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="tw-size-5 tw-flex-shrink-0"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h12A2.25 2.25 0 0 1 20.25 6v12A2.25 2.25 0 0 1 18 20.25H6A2.25 2.25 0 0 1 3.75 18V6Zm3.75 3h9m-9 4.5h9m-9 4.5h5.25" /></svg>;
}
function _temp6(item_0) {
  return <div key={item_0.key} className="tw-flex tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-lg tw-border-0 tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold">{item_0.label}</div>;
}
function _temp7(group_1) {
  return <div key={group_1.id} className="tw-flex tw-items-center tw-gap-x-1.5 tw-whitespace-nowrap tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset tw-ring-iron-600">{group_1.name}</div>;
}